import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { REST_STEP_SEC, formatRestTime } from '@heraion/shared';
import {
  Icon,
  IconButton,
  MeterRow,
  TimerControlsRow,
  useTimerDisplay,
  type TimerControl,
} from '../index';

import type { useRestTimer } from './useRestTimer';
import { learnedRest } from './state/learnedRestStore';
import type { useTheme } from './theme';

// The rest, as the transition (MX-5 slice 3b, D8). Two rows on the shared
// <MeterRow> grid the effort band and the hold clock also sit on: the word on
// the axis' left, the timer's commands on its right, the readout on the row
// below, the fixed column for the one destructive control. Its shape is the
// twin of <EffortAxisDuration>.
//
// ⚠️ It no longer lives inside the exercise card. TM-18c docks it to the bottom
// of the screen inside <LoggerRestBar>, which owns the frame, the measured
// height and the breakpoint; this file kept its internal geometry unchanged,
// which is why the asymmetric hitSlops below still guard the same neighbours —
// the ✕ moved house with the controls above it.
//
// What the athlete reads is NEVER colour alone: the word "Rest", the number
// falling, and the caption naming what comes next all carry it as text. The ✕
// is the way out, and it is not a discard — skipping the rest simply hands the
// focus over early, which is exactly what waiting would have done.
export function FocusPanelRest({
  restTimer,
  cueText,
  cuePinned = false,
  onOpenRestPicker,
  restPickerSaving,
  theme,
}: {
  restTimer: ReturnType<typeof useRestTimer>;
  /** "→ Set 3/4" / "→ <next exercise>". Null for a rest started by hand from
   *  the header, which transitions to nothing — the row then shows the
   *  countdown alone, which is all a manual rest ever meant. */
  cueText: string | null;
  /** #308: the target is the athlete's Pin; the caption wears its glyph. */
  cuePinned?: boolean;
  /** Opens the per-exercise rest-default picker — "the rest I'm actually
   *  taking is the right one, remember it". It rode on the deleted banner and
   *  moves here with it: dropping it would have quietly removed the only way
   *  to set a rep exercise's rest default mid-workout. */
  onOpenRestPicker: () => void;
  restPickerSaving: boolean;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const s = restTimer.state;
  const learnedSec = learnedRest.useLearned(s.exerciseId);
  // The ±30 changes the running rest AND, after a neutral set, teaches the
  // next one (#40). The length they set is learned, not the time elapsed.
  const adjust = (delta: number) => {
    restTimer.add(delta);
    learnedRest.learn(s.exerciseId, s.totalSec + delta);
  };
  // The one reader of the falling number (#326): this panel re-renders once
  // per second while the rest runs, the logger around it does not.
  const { displaySec: remainingSec } = useTimerDisplay(s.clock);

  const controls: TimerControl[] = [
    // −30 / +30: a rest runs long as often as it runs short (LG-29: the timer
    // only knew how to add). add() already clamps to 0, so subtracting past the
    // end simply lets the rest expire — the same outcome as ✕, reached by the
    // number instead of the skip. The step is the information, so it's a `label`
    // control, twin of the +30 (TimerControlsRow, "a step says its AMOUNT").
    {
      key: 'subtract',
      label: `-${REST_STEP_SEC}`,
      a11yLabel: t('mobile.logger.restSubtractStep', {
        n: REST_STEP_SEC,
        defaultValue: '−{{n}} seconds',
      }),
      onPress: () => adjust(-REST_STEP_SEC),
    },
    {
      key: 'add',
      label: `+${REST_STEP_SEC}`,
      a11yLabel: t('mobile.logger.restAddStep', {
        n: REST_STEP_SEC,
        defaultValue: '+{{n}} seconds',
      }),
      onPress: () => adjust(REST_STEP_SEC),
    },
    {
      key: 'pause',
      icon: s.isPaused ? 'play' : 'pause',
      a11yLabel: s.isPaused
        ? t('mobile.logger.restResume', { defaultValue: 'Resume' })
        : t('mobile.logger.restPause', { defaultValue: 'Pause' }),
      onPress: s.isPaused ? restTimer.resume : restTimer.pause,
    },
  ];
  // "Remember this rest for this exercise" — only when the countdown is bound
  // to one, which since TM-19 includes a rest started by hand from the header:
  // it binds to the exercise in focus, and only a rest taken with nothing in
  // focus has no row to edit. On a rep exercise this is the ONLY way in to the
  // rest default mid-workout: the ⚙ on the effort axis exists only for
  // duration sets, so losing this would have silently removed the affordance
  // for every ordinary lift.
  if (s.exerciseId && learnedSec !== undefined) {
    // «Ripristina»: forget the learned rest, back to the exercise default.
    // Visible only while there is something learned, so its disappearing is
    // the feedback.
    const exerciseId = s.exerciseId;
    controls.push({
      key: 'restoreLearned',
      icon: 'refresh',
      a11yLabel: t('mobile.logger.restRestoreLearnedA11y', {
        time: formatRestTime(learnedSec),
        defaultValue: 'Restore the default rest (now {{time}})',
      }),
      onPress: () => learnedRest.clear(exerciseId),
    });
  }
  if (s.exerciseId) {
    controls.push({
      key: 'edit',
      icon: 'settings',
      a11yLabel: t('mobile.logger.restEditA11y'),
      onPress: onOpenRestPicker,
      disabled: restPickerSaving,
    });
  }

  const restRemaining = t('mobile.logger.restRemainingA11y', {
    time: formatRestTime(remainingSec),
    defaultValue: '{{time}} of rest remaining',
  });
  const restRemainingA11y = s.isPaused
    ? t('mobile.logger.pausedReadoutA11y', {
        label: restRemaining,
        defaultValue: '{{label}}, paused',
      })
    : restRemaining;

  return (
    <View style={styles.block}>
      <View style={styles.axis}>
        <Text style={styles.word} numberOfLines={1}>
          {t('mobile.logger.restWord', { defaultValue: 'Rest' })}
        </Text>
        <TimerControlsRow
          theme={theme}
          controls={controls}
          defaultVariant="default"
          // ⚠️ bottom 4, not 8. These controls sit directly ABOVE the ✕ in the
          // column below — both are pinned to the row's right edge — and the
          // only thing between them is the axis' 4pt margin. Two 8pt claims
          // across a 4-8pt channel overlap, so a tap meant for "extend the
          // rest" could land on "skip it". Same asymmetric cap the hold axis'
          // gear already uses over the same column, for the same reason
          // (EffortAxisDuration, a11y audit 2026-07-22); 36 + 8 + 4 = 48 still
          // clears the touch floor.
          hitSlop={{ top: 8, bottom: 4, left: 4, right: 4 }}
          style={styles.controls}
        />
      </View>
      <MeterRow
        theme={theme}
        trailing={
          <View style={styles.skipWrap}>
            <IconButton
              theme={theme}
              variant="cancel"
              name="close"
              onPress={restTimer.reset}
              // Spoken as what it DOES, not as "close": it ends the rest and
              // moves you on. Nothing is thrown away here, unlike the ✕ that
              // cancels a running hold.
              accessibilityLabel={t('mobile.logger.restSkip', {
                defaultValue: 'Skip',
              })}
              // top 4 for the same reason the row above caps its bottom: the
              // two touch zones meet in a 4pt channel and must not overlap.
              hitSlop={{ top: 4, bottom: 8, left: 4, right: 8 }}
            />
          </View>
        }
      >
        <View style={styles.strip}>
          <Text
            style={[styles.count, s.isPaused && styles.countPaused]}
            numberOfLines={1}
            accessible
            // Not a live region: it changes every second and would talk over
            // everything else. Same stance as the hold clock.
            // TM-13c — pause is ORTHOGONAL to the reading, so it wraps it with
            // the house's shared "paused" key instead of a second sentence: the
            // ink2 + italic above says it to the eye, and this says it to the
            // screen reader, which otherwise hears a countdown that is frozen.
            accessibilityLabel={restRemainingA11y}
          >
            {formatRestTime(remainingSec)}
          </Text>
          {cueText ? (
            <View style={styles.cueBox}>
              {cuePinned ? (
                <Icon
                  name="pin"
                  size={theme.typography.sizes.sm}
                  color={theme.palette.brandText}
                  fill={theme.palette.brandText}
                />
              ) : null}
              <Text
                style={styles.cue}
                numberOfLines={1}
                accessibilityLabel={
                  cuePinned
                    ? `${cueText}, ${t('mobile.logger.pinned', { defaultValue: 'pinned' })}`
                    : undefined
                }
              >
                {cueText}
              </Text>
            </View>
          ) : null}
        </View>
      </MeterRow>
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // 4pt between the two rows, the same gap the effort block uses — the two
    // are read one after the other and should not measure differently.
    block: { gap: 4 },
    axis: {
      flexDirection: 'row',
      alignItems: 'center',
      // A narrow width or a big font drops the commands under the word instead
      // of squeezing it to "R…" (five controls once the restore shows).
      flexWrap: 'wrap',
      gap: s.s2,
      marginBottom: s.s2,
    },
    // Takes the free width so its tap-free label can't be squeezed by the
    // controls.
    word: {
      flexGrow: 1,
      flexShrink: 0,
      color: p.ink,
      fontSize: ty.sizes.base,
      fontWeight: '700',
    },
    // Packed to the RIGHT here, unlike the hold axis: a small cluster (−30 / +30
    // / pause / ⚙) and the column below holds the ✕, so the commands belong on
    // that same edge instead of floating in the middle of the row. TimerControlsRow
    // wraps them under the word if a narrow screen can't seat them on one line.
    controls: { justifyContent: 'flex-end', gap: s.s3 },
    // The band's stand-in: same rounded box, quiet surface. It is not
    // interactive, so it takes no pressed state and no ring.
    strip: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: s.s3,
      gap: s.s2,
      borderRadius: r.r3,
      backgroundColor: p.surface2,
    },
    // lg + mono + tabular: this is the number the athlete glances at across a
    // gym floor, and tabular digits keep it from twitching every second.
    count: {
      color: p.ink,
      fontFamily: ty.fonts.mono,
      fontSize: ty.sizes.lg,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },
    // Paused: a quieter INK, not an opacity. Dimming `ink` with alpha is a
    // blend, so it eats the contrast — 0.55 measured 4.18:1 on the light
    // families and even 0.75 only reached 3.52:1, under the floor on the one
    // number read at a glance across a room. `ink2` is checked by
    // pausedReadoutContrast.test.ts. Three cues and none of them colour alone:
    // quieter ink, italic, and the ‖ beside it has become ▶.
    countPaused: { color: p.ink2, fontStyle: 'italic' },
    // Fills MeterRow's fixed column — the width and the no-shrink live there,
    // in the one place the hold slot and the Fail box also read them from.
    // flex-end, NOT center: the ✕ sits under the commands on the axis, and both
    // have to end on the row's right edge, which is the whole reason MeterRow
    // was extracted. Centred in the 48pt column it landed ~6dp short of the
    // gear above it — invisible in a test, obvious in a screenshot measured at
    // 280dp. Same alignment the hold axis gives its own trailing slot.
    skipWrap: { flex: 1, alignItems: 'flex-end', justifyContent: 'center' },
    // The caption shrinks before the number does — the time is the fact, the
    // destination is the context.
    cueBox: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s1,
      flexShrink: 1,
    },
    cue: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: '700',
      flexShrink: 1,
    },
  });
}
